import { useEffect, useState } from "react";
import { MessageCircle, Send, Loader2 } from "lucide-react";
import { fetchPlanComments, addPlanComment } from "@/lib/api";

export default function PlanComments({ planId, testidPrefix = "" }) {
    const [comments, setComments] = useState([]);
    const [body, setBody] = useState("");
    const [posting, setPosting] = useState(false);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        fetchPlanComments(planId)
            .then((c) => {
                setComments(c);
                setLoaded(true);
            })
            .catch(() => setLoaded(true));
    }, [planId]);

    const submit = async (e) => {
        e.preventDefault();
        const text = body.trim();
        if (!text) return;
        setPosting(true);
        try {
            const fresh = await addPlanComment(planId, text);
            setComments((cs) => [...cs, fresh]);
            setBody("");
        } finally {
            setPosting(false);
        }
    };

    return (
        <div
            className="mt-4 pt-4 border-t border-[var(--border)]"
            data-testid={`${testidPrefix}comments-${planId}`}
        >
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--cyan)] mb-3 flex items-center gap-2">
                <MessageCircle size={11} />
                Class Chat · {comments.length}
            </div>
            <div className="space-y-2 mb-3 max-h-60 overflow-y-auto">
                {loaded && comments.length === 0 && (
                    <p className="font-mono text-xs text-[var(--muted)]">
                        be the first to post.
                    </p>
                )}
                {comments.map((c) => (
                    <div
                        key={c.id}
                        className="border-l-2 border-[var(--border)] pl-3 py-1"
                        data-testid={`comment-${c.id}`}
                    >
                        <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
                            <span
                                className={
                                    c.user_role === "teacher"
                                        ? "text-[var(--coral)]"
                                        : "text-[var(--lime)]"
                                }
                            >
                                {c.user_name || "anon"}
                                {c.user_role === "teacher" && " · teacher"}
                            </span>
                            <span className="ml-2">
                                {new Date(c.created_at).toLocaleString()}
                            </span>
                        </div>
                        <p className="font-mono text-sm text-white mt-1 whitespace-pre-wrap">
                            {c.body}
                        </p>
                    </div>
                ))}
            </div>
            <form onSubmit={submit} className="flex gap-2">
                <input
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="say something…"
                    maxLength={500}
                    className="input-line flex-1"
                    data-testid={`${testidPrefix}comment-input-${planId}`}
                />
                <button
                    type="submit"
                    disabled={posting || !body.trim()}
                    className="bg-[var(--lime)] text-black px-3 py-2 disabled:opacity-30 hover:bg-[var(--cyan)] transition-colors"
                    data-testid={`${testidPrefix}comment-send-${planId}`}
                >
                    {posting ? (
                        <Loader2 size={14} className="animate-spin" />
                    ) : (
                        <Send size={14} />
                    )}
                </button>
            </form>
        </div>
    );
}
